import type { LucideIcon, LucideProps } from 'lucide-react'

/**
 * The one place lucide is configured.
 *
 * Icons in this console are chrome, not content: each sits beside a text label
 * that already carries the meaning, so an icon is `aria-hidden` by default and a
 * screen reader never reads a decorative glyph twice. They are drawn at a 1.75px
 * stroke to match the hairline weight of the rest of the interface rather than
 * lucide's heavier default. The rare icon that stands alone — a control with no
 * visible text — passes `label`, which gives it an accessible name and promotes
 * it to `role="img"`.
 *
 * Wrapping lucide here (instead of importing glyphs directly at call sites) means
 * the size/stroke/a11y defaults live in one place, and swapping the icon set
 * later is a change to this file, not to fifty components.
 */
export interface IconProps extends Omit<LucideProps, 'ref'> {
  icon: LucideIcon
  /** Accessible name — set ONLY for an icon with no adjacent text label. */
  label?: string
}

export function Icon({ icon: Glyph, size = 16, strokeWidth = 1.75, label, ...rest }: IconProps) {
  return (
    <Glyph
      size={size}
      strokeWidth={strokeWidth}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
      {...rest}
    />
  )
}
